"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { AuthenticatedUser, ISchedule } from "@gettactic/api";
import { api } from "@/lib/api/api";

export function useAdminReservationMutations(
  schedule: ISchedule,
  authenticatedUser?: AuthenticatedUser
) {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [isCheckedOut, setIsCheckedOut] = useState(false);

  const targetSlot = useMemo(() => {
    const slot = schedule.slots.find(
      (slot) => slot.resource.id === schedule.resource.id
    );
    if (!slot) return slot;

    // this is used on a map and we have to enforce rerender
    if (isCheckedIn) {
      const newSlot = {
        ...slot,
        capabilities: {
          ...slot.capabilities,
          can_respond_presence_check_in: false
        }
      };
      return newSlot;
    }

    // this is used on a map and we have to enforce rerender
    if (isCheckedOut) {
      const newSlot = {
        ...slot,
        capabilities: {
          ...slot.capabilities,
          can_respond_presence_check_out: false
        }
      };
      return newSlot;
    }

    return slot;
  }, [schedule, isCheckedIn, isCheckedOut]);

  const cardIsSelfAdmin = authenticatedUser?.user?.id === targetSlot?.user.id;
  const checkinMutation = useMutation({
    mutationFn: async () => {
      setLoading(true);
      if (!targetSlot) {
        throw new Error("No target slot found");
      }
      // Presence is per user: one call covers every resource of the reservation.
      await api.client.schedules.checkinUser(schedule.id, targetSlot.user.id);
      await queryClient.invalidateQueries({
        queryKey: ["schedules"]
      });
      await queryClient.invalidateQueries({
        queryKey: ["myAgenda"]
      });
      await queryClient.invalidateQueries({
        queryKey: ["organizations.schedulable"]
      });
      await queryClient.invalidateQueries({
        queryKey: ["tableOverview"]
      });
    },

    onSettled: () => {
      setLoading(false);
    },

    onSuccess: (result) => {
      setIsCheckedIn(true);
      toast.success(
        cardIsSelfAdmin
          ? "You're checked in"
          : "Checked in on behalf of another user"
      );
    },

    onError: (result) => {
      toast.error(`Sorry we couldn't complete the check in`);
    }
  });
  const checkoutMutation = useMutation({
    mutationFn: async () => {
      setLoading(true);
      if (!targetSlot) {
        throw new Error("No target slot found");
      }
      await api.client.schedules.checkoutUser(schedule.id, targetSlot.user.id);
      await queryClient.invalidateQueries({
        queryKey: ["schedules"]
      });
    },

    onSettled: () => {
      setLoading(false);
    },

    onSuccess: (result) => {
      setIsCheckedOut(true);
      toast.success(
        cardIsSelfAdmin
          ? "You've checked out"
          : "Checked out on behalf of another user"
      );
    },

    onError: (result) => {
      toast.error(`Sorry we couldn't complete the checkout`);
    }
  });
  const onCheckin = useCallback(() => {
    checkinMutation.mutate();
  }, [checkinMutation]);
  const onCheckout = useCallback(() => {
    checkoutMutation.mutate();
  }, [checkoutMutation]);

  return {
    checkinMutation,
    checkoutMutation,
    onCheckin,
    onCheckout,
    loading,
    targetSlot
  };
}
